import toast from 'react-hot-toast';
import { COPIED_TO_CLIPBOARD } from '../constants';
import CustomLink from '../components/CustomLink';
import React from 'react';

export const {
  MODE,
  VITE_APP_VERSION,
  VITE_BASE_URL,
  VITE_API_V3,
  VITE_API_SERVER,
  VITE_URL,
  VITE_BASE_PUBLIC_PATH,
  VITE_PORT,
  VITE_CREATOR_PORT,
  VITE_VIEWER_PORT,
  VITE_CREATOR_URL,
  VITE_VIEWER_URL,
  VITE_SHOW_LANGUAGE_SELECTOR,
  VITE_SERVICE_TYPE,
} = import.meta.env;

export const isDev = MODE === 'development';

const getNavigatorInfo = () => {
  const { userAgent, maxTouchPoints } = navigator;
  const isMac = /Mac OS X/i.test(userAgent);
  let isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      userAgent
    ) ||
    (isMac && !!maxTouchPoints);

  return { isMobile };
};

export const isMobile = getNavigatorInfo().isMobile;

export const isSmallScreenMediaQuery =
  window.matchMedia('(max-width:767px)').matches;
export const isMediumScreenMediaQuery =
  window.matchMedia('(max-width:820px)').matches;
export const isLargeScreenMediaQuery =
  window.matchMedia('(max-width:900px)').matches;
export const isXLargeScreenMediaQuery =
  window.matchMedia('(max-width:1000px)').matches;
export const isXXLargeScreenMediaQuery =
  window.matchMedia('(max-width:1110px)').matches;

export const toastOptions = {
  duration: 2000,
  style: {
    padding: 0,
    width: '358px',
    height: '38px',
    background: 'rgba(66, 66, 66, 0.8)',
    borderRadius: '4px',
    fontFamily: 'Noto Sans KR',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '15px',
    lineHeight: '100%',
    color: '#fff',
  },
};

export const toastContainerStyle = {
  top: '59px',
};

export const formatBytes = (bytes: number, decimals: number = 2) => {
  if (!bytes) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const copyToClipboard = async (text: string) => {
  const notify = () => toast(COPIED_TO_CLIPBOARD);
  try {
    await navigator.clipboard.writeText(text);
    notify();
    console.log(`Copy To Clipboard: ${text}`);
  } catch (error) {
    console.log(`Fail Copy To Clipboard: ${error}`);
  }
};

export const checkEmailPattern = (text: string) => {
  const isEmail = /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(text);
  return isEmail;
};

export const maskEmail = (email: string) => {
  const [localPart, domainPart] = email.split('@');
  const [domain, tld] = domainPart.split('.');
  const maskedLocalPart =
    localPart.substring(0, 2) + '*'.repeat(localPart.length - 2);
  const maskedDomain = domain[0] + '*'.repeat(domain.length - 1);

  return `${maskedLocalPart}@${maskedDomain}.${tld}`;
};

export const regExPwd =
  /^(?!((?:[A-Za-z]+)|(?:[~!@#$%^&*()_+=?]+)|(?:[0-9]+))$)[A-Za-z\d~!@#$%^&*()_+=?]{8,20}$/;
export const regExName = /^\s*(?:[가-힣a-zA-Z]\s*){2,}$/;
export const regExYear = /(192[1-9]|19[3-9]\d|20[01]\d|202[0-3])$/;

export const wrapUrlsWithCustomLink = (text: string, classList: string[]) => {
  if (!text) return null;

  const urlPattern =
    /(?:https?:\/\/)?(?:www\.)?([a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,})(?::\d+)?(?:\/[^\s]*)?/g;
  const result: (string | JSX.Element)[] = [];
  let lastIndex = 0;
  let match;

  while ((match = urlPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }

    result.push(
      React.createElement(CustomLink, {
        key: `link_${match.index}`,
        url: match[0],
        classList,
      })
    );

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }

  return result;
};

export const createBlobFromURL = (dataURI: string) => {
  const blobArray = [];
  const inputMIME = dataURI.split(',')[0].split(':')[1].split(';')[0];
  let binaryVal;

  if (dataURI.split(',')[0].indexOf('base64') >= 0) {
    binaryVal = window.atob(dataURI.split(',')[1]);
  } else {
    binaryVal = decodeURIComponent(dataURI.split(',')[1]);
  }

  for (let index = 0; index < binaryVal.length; index++) {
    blobArray.push(binaryVal.charCodeAt(index));
  }
  const ta = new Uint8Array(blobArray);

  return new Blob([ta], {
    type: inputMIME,
  });
};

export const removeCharacters = (text: string) => {
  return text.replace(/[^0-9]/g, '');
};

export const getOnlyNumber = (text: string) => {
  return text.replace(/[^-0-9]/g, '');
};

export const removeNoAlphabet = (text: string) => {
  return text.replace(/[^ㄱ-ㅎ가-힣a-zA-Z\s]/g, '');
};

export const removeFileExtension = (filename: string) => {
  if (!filename) return '';

  const lastDotIndex = filename.lastIndexOf('.');
  return lastDotIndex > 0 ? filename.substring(0, lastDotIndex) : filename;
};

export const limitMaxLen = (value: string, length: number, isInt?: boolean) => {
  const len = isInt && value[0] === '-' ? length + 1 : length;
  return value.slice(0, len);
};

export const download = (projectId: number, isSharedLink?: boolean) => {
  const url = isSharedLink
    ? `${VITE_API_V3}/common/guest/resource/download/${projectId}`
    : `${VITE_BASE_URL}/archive/download/resource/${projectId}`;
  const anchor = document.createElement('a');

  anchor.style.display = 'none';
  anchor.href = url;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

export const downloadByUrl = (url: string, filename?: string) => {
  const anchor = document.createElement('a');

  if (filename) {
    anchor.download = filename;
  } else {
    anchor.setAttribute('download', '');
  }

  anchor.style.display = 'none';
  anchor.href = url;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

export const openNewWindow = (url: string, options?: string) => {
  const width = window.outerWidth;
  const height = window.outerHeight;

  window.open(
    !url.includes('http') ? `https://${url}` : url,
    '_blank',
    options ?? `width=${width} height=${height} noopener noreferrer`
  );
};

export const getRandomStrOfNumbers = (length: number) => {
  return Math.random()
    .toString(10)
    .substring(2, length + 2);
};

export const createInput = (
  form: HTMLFormElement,
  name: string,
  value: string
) => {
  const input = document.createElement('input');
  input.type = 'hidden';
  input.name = name;
  input.value = value;
  form.appendChild(input);
};

export const createForm = (
  {
    target,
    method,
    action,
  }: { target: string; method: string; action: string },
  inputList: { name: string; value: string }[]
) => {
  const form = document.createElement('form');

  form.target = target;
  form.method = method;
  form.action = action;
  form.style.display = 'none';

  inputList.map(({ name, value }) => {
    createInput(form, name, value);
  });

  return form;
};

export const getFileType = (ext: string) => {
  const imageTypes = [
    'jpg',
    'jpeg',
    'png',
    'bmp',
    'gif',
    'svg',
    // 'apng',
    // 'avif',
    // 'ico',
    // 'tif',
    // 'tiff',
    // 'webp',
  ];
  const audioTypes = [
    'mp3',
    'wav',
    // 'aac',
    // 'alac',
    // 'dsd',
    // 'flac',
    // 'mqa',
    // 'm4a',
    // 'ogg',
    // 'wma',
  ];
  const videoTypes = [
    'mp4',
    // 'avchd',
    // 'avi',
    // 'divx',
    // 'flv',
    // 'mkv',
    // 'mov',
    // 'm4v',
    // 'mpeg',
    // 'm2ts',
    // 'mts',
    // 'ts',
    // 'webm',
    // 'wmv',
  ];
  const compressionTypes = ['zip', 'apk', 'rar', '7z', 'ace', 'alz', 'egg'];
  const typeList = [
    'folder',
    'folder_using',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'doc',
    'docx',
    'pdf',
    'hwp',
    'hwpx',
    'epub',
    'web',
    'etc',
  ];
  const type = imageTypes.includes(ext)
    ? 'image'
    : audioTypes.includes(ext)
      ? 'audio'
      : videoTypes.includes(ext)
        ? 'video'
        : compressionTypes.includes(ext)
          ? 'zip'
          : typeList.includes(ext)
            ? ext
            : 'etc';

  return type;
};

export const convertingSupportList = [
  'pdf',
  // 'hwp',
  // 'hwpx',
  // 'doc',
  // 'docm',
  // 'docx',
  // 'dotx',
  // 'dot',
  // 'odt',
  // 'pot',
  // 'pps',
  // 'ppsx',
  // 'ppt',
  // 'pptx',
];

export const getIsTimeInRange = (
  start: string = '2024-07-29T09:30:00+09:00',
  end: string = '2024-08-12T23:30:00+09:00'
) => {
  const now = new Date();
  const koreanTime = new Date(
    now.toLocaleString('en-US', { timeZone: 'Asia/Seoul' })
  );
  const startDate = new Date(start);
  const endDate = new Date(end);
  const isInRange = koreanTime >= startDate && koreanTime <= endDate;

  return isInRange;
};

export const getTopLevels = (values: number[]) => {
  const activeValues = values.filter((value) => value > 0);
  const sortedValues = [...activeValues].sort((a, b) => b - a);
  const uniqueValues = [...new Set(sortedValues)];

  const highest: number[] = [];
  const second: number[] = [];

  if (uniqueValues.length > 0) {
    highest.push(uniqueValues[0]);
  }
  if (uniqueValues.length > 1) {
    second.push(uniqueValues[1]);
  }
  if (uniqueValues.length > 2) {
    second.push(uniqueValues[2]);
  }

  return { highest, second };
};
